import React, { useEffect, useRef, useState } from "react";
import { capture } from "../../../analytics";
import { gql, useQuery, useMutation } from "@apollo/client";
import {
  getStoredInviteCode,
  clearStoredInviteCode,
} from "../../../referral";

const MY_SUBSCRIPTION_STATUS = gql`
  query MySubscriptionStatusForBanner {
    mySubscriptionStatus {
      hasActiveSubscription
    }
  }
`;

const MY_ACCESS = gql`
  query MyAccessForBanner {
    myAccess {
      member
      paywalled
    }
  }
`;

const CREATE_MY_CHECKOUT_SESSION = gql`
  mutation CreateMyCheckoutSessionForBanner($returnUrl: String!) {
    createMyCheckoutSession(returnUrl: $returnUrl)
  }
`;

const REDEEM_INVITE_CODE = gql`
  mutation RedeemInviteCodeFromBanner($code: String!) {
    redeemInviteCode(code: $code) {
      ok
      message
      member
    }
  }
`;

// Compact CTA shown at the top of the Events tab for users who don't
// currently have an active Stripe subscription. States:
//   - sex unanswered -> "finish your profile" prompt (opens the
//     t-shirt/sex modal via onNeedInfo); the subscribe button stays
//     hidden until the question is answered.
//   - sex === "female":
//       * member standing (approved / legacy free access) -> nothing
//       * ladiesPaywall flag ON and not yet a member -> the same
//         Subscribe button everyone sees, plus "Have an invite code?"
//         (a code from /signup?code=… auto-redeems on first load)
//       * flag OFF -> nothing (women-free policy, legacy behavior)
//   - sex === "male"  -> Subscribe button (Stripe Checkout).
// Auto-hides entirely for active subscribers.
const SubscribeBanner = ({ userInfo, onNeedInfo }) => {
  const { data, loading } = useQuery(MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: "network-only",
  });
  const {
    data: accessData,
    loading: accessLoading,
    refetch: refetchAccess,
  } = useQuery(MY_ACCESS, { fetchPolicy: "network-only" });
  const [createCheckout, { loading: checkoutLoading }] = useMutation(
    CREATE_MY_CHECKOUT_SESSION
  );
  const [redeemCode, { loading: redeeming }] = useMutation(REDEEM_INVITE_CODE);
  const [error, setError] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [codeNote, setCodeNote] = useState("");
  const autoTried = useRef(false);

  const sex = String(userInfo?.sex || "").trim().toLowerCase();
  const member = accessData?.myAccess?.member === true;
  const paywalled = accessData?.myAccess?.paywalled === true;

  const submitCode = async (code) => {
    const clean = String(code || "").trim().toUpperCase();
    if (!clean) return;
    setError("");
    setCodeNote("");
    try {
      const { data: out } = await redeemCode({ variables: { code: clean } });
      const r = out?.redeemInviteCode;
      if (r?.ok) {
        clearStoredInviteCode();
        capture("invite_code_redeemed", { member: !!r.member });
        setCodeNote(r.message || "Code applied.");
        if (r.member) {
          // Standing changed server-side; a full reload re-reads
          // events/locations/chat with the new access.
          setTimeout(() => window.location.reload(), 900);
        } else {
          refetchAccess();
        }
      } else {
        setCodeNote(r?.message || "That code didn't work.");
      }
    } catch (err) {
      setCodeNote(err.message || "Couldn't apply that code.");
    }
  };

  // Auto-redeem a code captured on /signup?code=… — once, as soon as
  // we know the person isn't already a member.
  useEffect(() => {
    if (autoTried.current || accessLoading || !accessData) return;
    const stored = getStoredInviteCode();
    if (!stored) return;
    autoTried.current = true;
    if (member) {
      clearStoredInviteCode();
      return;
    }
    submitCode(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLoading, accessData, member]);

  if (loading || !data) return null;
  if (data.mySubscriptionStatus?.hasActiveSubscription) return null;

  // Women: free access is the legacy rule; under the paywall flag a
  // non-member woman sees the same subscribe CTA plus the code box.
  if (sex === "female") {
    if (accessLoading || !accessData) return null;
    if (member || !paywalled) return null;
  }

  // Not answered yet (legacy rows may carry "default") — prompt for
  // the profile questions instead of showing a subscribe button.
  if (sex !== "male" && sex !== "female") {
    return (
      <div
        style={{
          marginBottom: 10,
          padding: "6px 12px",
          background: "#eef4fb",
          border: "1px solid #bcd4ef",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <span style={{ color: "#1d3d5f", fontSize: 13, fontWeight: 600 }}>
          Finish setting up your profile
        </span>
        <button
          type="button"
          onClick={() => onNeedInfo && onNeedInfo()}
          style={{
            padding: "5px 12px",
            background: "#1f6feb",
            color: "white",
            border: "none",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Get started
        </button>
      </div>
    );
  }

  const goCheckout = async () => {
    capture("checkout_started", { source: "events_banner" });
    setError("");
    const returnUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}`
        : "https://activebeaches.com/dashboard";
    try {
      const { data: out } = await createCheckout({ variables: { returnUrl } });
      const url = out?.createMyCheckoutSession;
      if (url) {
        window.location.href = url;
      } else {
        setError("Stripe returned no checkout URL.");
      }
    } catch (err) {
      setError(err.message || "Couldn't start checkout.");
    }
  };

  const showCodeBox = sex === "female" && paywalled && !member;

  return (
    <div
      style={{
        marginBottom: 10,
        padding: "6px 12px",
        background: "#eef7ee",
        border: "1px solid #b9dcb5",
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{ color: "#1f4f1d", fontSize: 13, fontWeight: 600 }}
          title="See private locations, RSVPs, and our GroupMe chat."
        >
          Subscribe to unlock event details
        </span>
        <button
          type="button"
          onClick={goCheckout}
          disabled={checkoutLoading}
          style={{
            padding: "5px 12px",
            background: "#1f7a3a",
            color: "white",
            border: "none",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: checkoutLoading ? "default" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {checkoutLoading ? "Opening…" : "Subscribe · $20/mo"}
        </button>
      </div>
      {showCodeBox && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitCode(codeInput);
          }}
          style={{
            marginTop: 8,
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 12.5, color: "#3a4150" }}>
            Have an invite code?
          </span>
          <input
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            placeholder="ABCD2345"
            maxLength={16}
            style={{
              padding: "5px 8px",
              border: "1px solid #c9d0dc",
              borderRadius: 6,
              fontSize: 13,
              letterSpacing: "0.08em",
              width: 130,
              textTransform: "uppercase",
            }}
          />
          <button
            type="submit"
            disabled={redeeming || !codeInput.trim()}
            style={{
              padding: "5px 12px",
              background: "#fff",
              color: "#1f7a3a",
              border: "1px solid #1f7a3a",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {redeeming ? "Checking…" : "Apply"}
          </button>
          {codeNote && (
            <span style={{ fontSize: 12.5, color: "#1f4f1d" }}>{codeNote}</span>
          )}
        </form>
      )}
      {error && (
        <div style={{ marginTop: 6, fontSize: 12.5, color: "#b02a2a" }}>{error}</div>
      )}
    </div>
  );
};

export default SubscribeBanner;
